// React imports
import { useState, useEffect } from 'react';

// Custom component imports
import { DefaultFilters, DefaultFilterControls } from '../models/BullFilter'
import { getAppliedFilters } from '../logic/AppliedFilterLogic';
import { calculateAverages } from '../logic/AverageLogic';

// Service imports
import * as BullseyeAPI from '../services/BullseyeAPIService'
import * as BullseyeFS from '../services/BullseyeFilterService'
import { DefaultBullColumns, DefaultWeightedIndex } from '../models/BullColumns';
import computeCustomIndexForRow from '../logic/IndexCalculation';
import { CalculateSelectedBullCount } from '../logic/BullCountLogic';

// Context to be used for all state handling for the bull grid
function UseBullGrid() {
    // Holds the view state for all the bull data that was pulled from the API
    const [bullData, setBullData] = useState([]);

    // Holds the view state of the filter being used to filter the bull grid
    const [bullFilter, setBullFilter] = useState(DefaultFilters);

    // Holds the view state of the applied filters for the current state/bull grid
    const [appliedFilters, setAppliedFilters] = useState([]);

    // Holds the view state for the bull data to be shown upon filtering in the bull grid 
    const [filteredBullData, setFilteredBullData] = useState([])
    // Raw filtered data (source from filter service). We normalize/annotate this into `filteredBullData`.
    const [rawFilteredBullData, setRawFilteredBullData] = useState([])

    // Holds the view state of the trait min and max values for each individual trait
    const [bullTraitMinMax, setBullTraitMinMax] = useState([]);
    // Holds PTA standard deviations fetched from the API (preferred SD source)
    const [ptaStdDevs, setPtaStdDevs] = useState([]);

    // Holds the view state of the grid columns that need to be shown to the user
    const [gridDisplayColumns, setGridDisplayColumns] = useState(DefaultBullColumns)

    // Holds the view state of the custom index weights
    // Normalize weights so absolute values sum to 100 and clamp to [-100,100]
    // Preserve a stable ordering for traits based on the DefaultWeightedIndex definition
    const customIndexTraitOrder = Object.keys(DefaultWeightedIndex || {});
    function normalizeWeights(weights) {
        // Clamp inputs to integer range [-100,100]
        const clamped = {};
        for (const [k, v] of Object.entries(weights)) {
            let n = Math.round(Number(v) || 0);
            if (n > 100) n = 100;
            if (n < -100) n = -100;
            clamped[k] = n;
        }

    // Preserve clamped integer values and original key order. Do not scale to sum to 100.
    const normalized = {};
    const keysInOrder = Object.keys(clamped);
    for (const k of keysInOrder) normalized[k] = clamped[k];
    return normalized;
    }

    const [customIndexWeights, setCustomIndexWeights] = useState(() => normalizeWeights(DefaultWeightedIndex))
    // Toggle to enable/disable custom index computation and column usage (default off)
    const [useCustomIndex, setUseCustomIndex] = useState(false);
    // Holds range filter for the computed CustomIndex column
    const [customIndexRange, setCustomIndexRange] = useState(null);
    // Holds the global min/max bounds for the computed CustomIndex (pre-filter)
    const [customIndexBounds, setCustomIndexBounds] = useState([0, 0]);

    // Holds the view state of the total number of bulls that have been pulled for the grid
    const [totalBullCount, setTotalBullCount] = useState(0);

    // Holds the view state of the total number of bulls remaining in the list upon filtering
    const [selectedBullCount, setSelectedBullCount] = useState(0);

    // Holds the view state of true if the bull data is loading or hasn't yet loaded otherwise false if loaded in
    const [isBullDataLoading, setIsBullDataLoading] = useState(true);

    // Holds the view state of true if the bull filters are loading or haven't yet loaded otherwise false if loaded in
    const [isBullFiltersLoading, setIsBullFiltersLoading] = useState(true);

    // Holds the view state of true if the AI organizations are loading or haven't yet loaded otherwise false if loaded in
    const [isAiOrganizationsLoading, setIsAiOrganizationsLoading] = useState(true);

    // Holds the view state of true if the filter default values are loading or haven't yet loaded otherwise false if loaded in
    const [filterDefaultsLoading, setFilterDefaultsLoading] = useState(true);

    // Holds the view state of the grid api that is used to export and manipulate the bull grid
    const [gridApi, setGridApi] = useState(null);

    // Holds the view state of the column grid api that is used to manipulate the bull grid columns
    const [gridColumnApi, setGridColumnApi] = useState(null);

    // Holds the view state of the filter panel that was last used
    const [activeFilterPanel, setActiveFilterPanel] = useState(0);

    // Makes the API call to get the AI organizations
    // Makes the API call to get the bull trait min/max values 
    // Makes the API call to get the bull list
    useEffect(() => {   
        // Pull in the AI organizations from the API
        BullseyeAPI.getAIOrganizations().then((res) => {
            var aiSection = {
                sectionHeader: "A.I. Organizations",
                options: [
                ]
            };
            var aiOrganizations = JSON.parse(res.data);
            if (aiOrganizations.length > 0) {
                for (var j = 0; j < aiOrganizations.length; j++) {
                    DefaultFilterControls[0].sections[8].options.push({
                        type: "checkbox",
                        name: `chkAI_${aiOrganizations[j].RandomName.replace(" ", "")}_${aiOrganizations[j].NaabCodeList.replace([" ", "  "], "")}`,
                        label: `${aiOrganizations[j].RandomName} (${aiOrganizations[j].NaabCodeList})`,
                    });
                    Object.defineProperty(DefaultFilters, `chkAI_${aiOrganizations[j].RandomName.replace(" ", "")}_${aiOrganizations[j].NaabCodeList.replace([" ", "  "], "")}`, {
                        value: false,
                        writable: true,
                        enumerable : true, 
                    });
                }
            }
            setIsAiOrganizationsLoading(false);
        })

        // Pull in the bull trait min and max values from the API
        BullseyeAPI.getBullTraitMinMax().then((res) => {
            let deserializedData = JSON.parse(res.data);
            setBullTraitMinMax(deserializedData);
            setIsBullFiltersLoading(false);
        })

        // Pull in the PTA standard deviations from the API (preferred for index SDs)
        BullseyeAPI.getPtaStandardDeviations().then((res) => {
            let deserializedSd = JSON.parse(res.data);
            setPtaStdDevs(deserializedSd);
        }).catch(() => {
            // If SD endpoint fails, we'll fall back to bullTraitMinMax later when computing
        });

        // Pull in the list of bulls from the API
        BullseyeAPI.getBullList().then((res) => {
            let deserializedData = JSON.parse(res.data);
            setBullData(deserializedData);
            // Use rawFilteredBullData as the source of truth for normalization/annotation
            setRawFilteredBullData(deserializedData);
            setIsBullDataLoading(false);
        });

    }, []);

    // Updates the bull trait sliders with defaults of the min and max values pulled from the API
    useEffect(() => {
        if (bullTraitMinMax[0] != null) {
            // Iterate over the bull filter and set the values
            for (const [key, value] of Object.entries(DefaultFilters)) {
                // Check to make sure that there are max and min values available
                if (bullTraitMinMax[0][`${key}MIN`] !== null && bullTraitMinMax[0][`${key}MAX`]) {
                    bullFilter[key] = [bullTraitMinMax[0][`${key}MIN`], bullTraitMinMax[0][`${key}MAX`]];
                }
            }
            setFilterDefaultsLoading(false);
        }

    }, [bullTraitMinMax])

    // Sets the total bull count state value when the bull data list is reloaded 
    useEffect(() => {
        setTotalBullCount(bullData.length);
    }, [bullData]);

    // Fires the filter bull data method whenever the bull filter is changed/updated 
    useEffect(() => {
        async function fetchData() {
            var filteredData = await BullseyeFS.filterBullData(bullData, bullFilter);
            // write to raw source; the normalization/annotation effect will produce `filteredBullData`
            setRawFilteredBullData(filteredData || []);
            return filteredData;
        }

        fetchData();
        setAppliedFilters(getAppliedFilters(bullFilter, DefaultFilters));
    }, [bullFilter]);

    // Normalize raw filtered data and annotate rows with computed values (single source of updates).
    useEffect(() => {
        const source = Array.isArray(rawFilteredBullData) ? rawFilteredBullData : [];

        // Clean null/undefined entries (source may contain undefined during transitions)
        const cleaned = source.filter(r => r != null);

    // Calculate selected count (non-average rows)
    // NOTE: moved below to reflect CustomIndex range filtering


            // Compute custom index for each base row
            const baseRows = cleaned.slice();
            // Prefer PTA SDs from API; fall back to bullTraitMinMax[0] if not available
            const sdSource = (Array.isArray(ptaStdDevs) && ptaStdDevs[0]) ? ptaStdDevs[0] : ((Array.isArray(bullTraitMinMax) && bullTraitMinMax[0]) ? bullTraitMinMax[0] : {});

            // Annotate each row with computed CustomIndexValue (or null if disabled)
            const annotated = baseRows.map(r => {
                if (!useCustomIndex) return { ...r, CustomIndexValue: null };
                const rawVal = computeCustomIndexForRow(r, customIndexWeights, sdSource);
                const rounded = Math.round((Number(rawVal) || 0) * 100) / 100;
                return {
                    ...r,
                    CustomIndexValue: rounded
                };
            });

            // Compute global bounds from the annotated values (exclude average placeholder)
            if (useCustomIndex) {
                const vals = annotated.map(r => (r.CustomIndexValue == null) ? null : Number(r.CustomIndexValue)).filter(v => v != null && !isNaN(v));
                if (vals.length > 0) {
                    const min = Math.min(...vals);
                    const max = Math.max(...vals);
                    // Round bounds to whole numbers: min down, max up
                    const intMin = Math.floor(min);
                    const intMax = Math.ceil(max);
                    setCustomIndexBounds([intMin, intMax]);
                } else {
                    setCustomIndexBounds([0, 0]);
                }
            } else {
                setCustomIndexBounds([0, 0]);
            }

            // If a CustomIndex range is present in bullFilter, apply it now against
            // the annotated values so rows without a computed value are excluded.
            let postIndexFiltered = annotated;
            if (useCustomIndex && bullFilter && bullFilter.CustomIndex != null) {
                // Use rounded integer endpoints for filtering: floor(min), ceil(max)
                const min = Math.floor(Number(bullFilter.CustomIndex[0]));
                const max = Math.ceil(Number(bullFilter.CustomIndex[1]));
                postIndexFiltered = annotated.filter(item => {
                    const v = (item.CustomIndexValue == null) ? null : Number(item.CustomIndexValue);
                    if (v == null || isNaN(v)) return false;
                    return v >= min && v <= max;
                });
            }

            // Update the selected count now that the CustomIndex range has been applied
            setSelectedBullCount(CalculateSelectedBullCount(postIndexFiltered));

            // Recompute averages from the post-index-filtered set so the avg row matches
            // what the user sees in the grid.
            const avg = calculateAverages(postIndexFiltered) || {};
            if (!useCustomIndex) {
                avg.CustomIndexValue = null;
            } else {
                const avgRaw = computeCustomIndexForRow(avg, customIndexWeights, sdSource);
                avg.CustomIndexValue = Math.round((Number(avgRaw) || 0) * 100) / 100;
            }

        const normalized = [...postIndexFiltered, avg];

        // Only update if the result truly differs to avoid loops
        try {
            if (JSON.stringify(normalized) !== JSON.stringify(filteredBullData)) {
                setFilteredBullData(normalized);
            }
        } catch (e) {
            if (normalized.length !== filteredBullData.length) setFilteredBullData(normalized);
        }
    }, [rawFilteredBullData, customIndexWeights, useCustomIndex, bullFilter]);

    // update custom index range from slider
    useEffect(() => {
        if (customIndexRange == null) return;
        // write rawFilteredBullData to trigger re-filter (filtering effect will run elsewhere)
        // We rely on filterBullData -> setRawFilteredBullData in the existing effect chain, so
        // add nothing here; the filter service will consume bullFilter.CustomIndex directly.
    }, [customIndexRange]);

    // Sets the grid selections on bull ready and autosizes all the columns
    function onBullGridReady(params) {
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);
    }

    // Exports the bull grid to a CSV file
    function exportBullGrid() {
        gridApi.exportDataAsCsv();
    }

    // Sets the last active filter panel key to the current panel that is opened
    async function handleFilterPanelChanged(e, panelKey) {
        setActiveFilterPanel(panelKey);
    }

    function resetAppliedFilter(e, filterKey) {
        updateBullFilter(filterKey, DefaultFilters[filterKey]);
    }

    /* Resets the bull filter back to default values */
    function resetBullFilter() {
        setBullFilter(DefaultFilters);
    }

    // Resets the columns filter back to default values
    function resetColumns() {
        setGridDisplayColumns(DefaultBullColumns);
    }

    // Update a single custom index weight
    function updateCustomIndexWeight(name, value) {
        // clamp input and set the target weight only
        let v = Math.round(Number(value) || 0);
        if (v > 100) v = 100;
        if (v < -100) v = -100;

        const next = {
            ...customIndexWeights,
            [name]: v
        };
        setCustomIndexWeights(normalizeWeights(next));
    }

    // Reset custom index weights to defaults
    function resetCustomIndexWeights() {
        setCustomIndexWeights(normalizeWeights(DefaultWeightedIndex));
        // Also reset any CustomIndex range selection so the slider returns to default
        try {
            updateBullFilter('CustomIndex', null);
        } catch (e) {
            // If updateBullFilter isn't available for any reason, fall back to direct set
            setBullFilter({
                ...bullFilter,
                CustomIndex: null
            });
        }
    }

    // Toggle use of custom index; when disabled hide the column and stop computing
    function updateUseCustomIndex(value) {
        setUseCustomIndex(!!value);
        setGridDisplayColumns({
            ...gridDisplayColumns,
            chkDisplay_CustomIndex: !!value
        });
    }

    // computeCustomIndexForRow is provided by ../logic/IndexCalculation

    // Function that is called when the filter is changed to update the bull filter model
    async function handleCheckboxChange(e) {
        updateBullFilter(e.target.name, e.target.checked);
    }

    // Function that is called when the select all/deselect all checkbox is changed
    async function handleSelectAllCheckboxChange(checked, section) {
        let keys = [section.checkboxHeader.name];
        for (let k = 0; k < section['options'].length; ++k) {
            keys.push(section['options'][k].name);
        }
    
        let update = Object.assign({}, bullFilter);
        for (let i = 0; i < keys.length; ++i) {
            let key = keys[i];
            update[key] = checked;
        } 

        setBullFilter({
            ...bullFilter,
            ...update
        });
    }

    // Function that is called when the column display options have changed to update the grid display columns
    async function handleDisplayColumnCheckboxChange(e) {
        setGridDisplayColumns({
            ...gridDisplayColumns,
            [e.target.name]: e.target.checked
        });
    }

    // Function that is called when the filter is changed in order to update the bull filter model
    async function handleSliderInputChange(e, newValue, name) {
        updateBullFilter(name, newValue);
    }

    // Function that updates the bull filter object attribute with the correct value
    async function updateBullFilter(name, value) {
        setBullFilter({
            ...bullFilter,
            [name]: value
        });
    }

    /* Export variables, methods etc to be used as the bull grid context with the bullgrid/bullfilter components */
    return {
        bullData,
        setBullData,
        isBullDataLoading,
        setIsBullDataLoading,
        bullFilter,
        setBullFilter,
        filteredBullData,
        setFilteredBullData,
        isBullFiltersLoading,
        bullTraitMinMax,
        totalBullCount,
        selectedBullCount,
        handleSliderInputChange,
        handleCheckboxChange,
        resetBullFilter,
        exportBullGrid,
        onBullGridReady,
        handleDisplayColumnCheckboxChange,
        gridDisplayColumns,
        resetColumns,
    customIndexWeights,
    customIndexTraitOrder,
    updateCustomIndexWeight,
    resetCustomIndexWeights,
    useCustomIndex,
    updateUseCustomIndex,
    customIndexBounds,
    ptaStdDevs,
        isAiOrganizationsLoading,
        gridApi,
        filterDefaultsLoading,
        activeFilterPanel,
        handleFilterPanelChanged,
        appliedFilters,
        resetAppliedFilter,
        handleSelectAllCheckboxChange
    }
}

export default UseBullGrid